import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Terminal, Mail } from "lucide-react";

export default function Verify() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState(state?.email || "");
  const [code, setCode] = useState(state?.code || "");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [emailSent, setEmailSent] = useState(state?.emailSent || false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify", { email, code });
      login(data.token, data.user);
      toast.success("Verified. Welcome to the floor.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (!email) return toast.error("Enter your email first");
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-code", { email });
      if (data.email_sent) { toast.success("New code sent to your email"); setEmailSent(true); }
      else if (data.verification_code) { toast.info(`Dev code: ${data.verification_code}`); setCode(data.verification_code); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 text-fg">
      <div className="max-w-md w-full" data-testid="verify-page">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <Terminal size={18} className="text-amber"/>
          <div className="font-display text-xl text-amber">COYOTE/LIFE</div>
        </Link>
        <div className="label-xs text-amber">&gt; STEP 02 / 02</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none mt-2 flex items-center gap-3">
          <ShieldCheck size={32} strokeWidth={1.5} className="text-amber"/> Verify.
        </h1>
        <p className="mt-3 text-fg-muted font-mono text-sm">
          {emailSent ? <><Mail size={12} className="inline mr-1"/>Code sent to your inbox.</> : "Enter the 4-digit code."}
        </p>

        {state?.code && !emailSent && (
          <div className="mt-6 border border-warn bg-[#1A1500] p-3 font-mono text-xs text-warn" data-testid="verify-code-display">
            DEV CODE: <span className="font-bold tracking-widest text-base">{state.code}</span>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="label-xs mb-1 block">Email</label>
            <input type="email" className="input-term" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="verify-email-input" />
          </div>
          <div>
            <label className="label-xs mb-1 block">Code</label>
            <input className="input-term text-2xl tracking-[0.5em] text-center text-amber" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0,4))} maxLength={4} required data-testid="verify-code-input" />
          </div>
          <button type="submit" disabled={loading} data-testid="verify-submit-btn" className="btn-primary w-full">
            {loading ? "Verifying..." : "Verify & Enter >"}
          </button>
          <button type="button" onClick={resend} disabled={resending} className="btn-ghost w-full" data-testid="verify-resend-btn">
            {resending ? "Sending..." : "Resend code"}
          </button>
        </form>
      </div>
    </div>
  );
}
